/**
 * 2027年度予算 v8(スプレッドシート)の事業部シートから、予算を読み取る(2026-10-23 段階5・仮決定 K7)。
 * 全体設計 10章・12章。読み取ったものは予算の画面の表に入れるだけで、保存は今までどおり「保存」ボタンで行う。
 *
 * v8 の事業部シートの形: 区分(大項目) | 科目①(中項目) | 科目②(小項目) | 明細(科目③) | 1月 … 12月 | 1Q … 4Q
 *  - 見出しの行 = 月として読めるセルが12個以上ある行(「2027-01-01」「2027/1」「2027年1月」「1月」のどれでも)。その最初の12列を月とする
 *  - 「科目①」の列 = 見出しに「科目①」か「中項目」を含む列(無ければ月の列より左で2番目の列)。「科目②」も同じ(「科目②」「小項目」)
 *  - 集計の行(科目②に「計」「率」を含む・《》で始まる・合計の行)は読まない(明細の行を足す。二重に数えない)
 *  - 区分×科目①ごとに月別に足し、Gate の勘定科目(科目①)に合わせる。同じ名前が区分違いで2つある(原価の給料手当・販管の給料手当など)ため、
 *    区分(Gate の「項目」item_category)が同じものの中で、名前・コード・略称で探す。呼び方の違う2つ(仕入=仕入高・販管の給料手当=給与手当)は読み替える。
 *    合わない科目①は「マスタに無い」として一覧に出し、入れない
 *  - 金額: カンマ・円記号・空白は無視。マイナスは「-」「△」「▲」。小数は四捨五入。空欄・「-」は0
 *
 * 具体例は tests/unit/budgetImport.test.ts
 */
import type { Account, MonthStr } from '@core/lib/types';

export type V8BudgetRow = {
  /** v8 の区分(売上・原価・販管・外収・外費・特利・特損) */
  kubun: string;
  /** v8 の科目①の名前 */
  name: string;
  /** 合わせた Gate の勘定科目(合わなければ null) */
  accountId: string | null;
  /** 年度の月の順(12個) */
  amounts: number[];
  total: number;
  /** 足した明細の行数 */
  lineCount: number;
};

export type V8BudgetParse = {
  rows: V8BudgetRow[];
  /** 見出しに年月が書いてあり、選んだ年度と合わないときの注意 */
  warning: string | null;
  error: string | null;
};

const nk = (s: string) => s.normalize('NFKC').replace(/\s/g, '').toLowerCase();

/** 見出しのセルが月なら、その月(1〜12)と年(書いてあれば) */
export function monthOfHeader(raw: string): { year: number | null; month: number } | null {
  const s = raw.normalize('NFKC').trim();
  let m = /^(\d{4})[-/年.](\d{1,2})(?:[-/月.](\d{1,2})?.*)?$/.exec(s);
  if (m) {
    const month = Number(m[2]);
    return month >= 1 && month <= 12 ? { year: Number(m[1]), month } : null;
  }
  m = /^(\d{1,2})月$/.exec(s);
  if (m) {
    const month = Number(m[1]);
    return month >= 1 && month <= 12 ? { year: null, month } : null;
  }
  return null;
}

/** 金額のセル。読めないものは null(空欄・「-」は 0) */
export function parseBudgetAmount(raw: string): number | null {
  const s = raw.normalize('NFKC').trim().replace(/[,¥円\s]/g, '');
  if (s === '' || s === '-') return 0;
  const neg = /^[-△▲]/.test(s);
  const body = s.replace(/^[-△▲]/, '');
  if (!/^\d+(\.\d+)?$/.test(body)) return null;
  const n = Math.round(Number(body));
  return neg ? -n : n;
}

/** 集計の行か(読まない行) */
function isSummaryRow(kubun: string, name1: string, name2: string): boolean {
  const s = nk(name2);
  // 「売上高　計」「小計」「合計」「仕入　率」のように末尾が 計・率 のものだけ(「会計顧問料」「設計費」は明細)
  return (
    /(計|率)$/.test(s) ||
    s.startsWith('《') ||
    /合計|利益/.test(nk(kubun) + nk(name1)) ||
    (!name1 && !name2)
  );
}

export function parseV8Budget(params: {
  /** 貼り付けた表(行×セル) */
  table: string[][];
  /** 取り込む年度の月(年度の始まりから12か月) */
  months: MonthStr[];
  accounts: Array<Pick<Account, 'id' | 'name' | 'active'> & Partial<Pick<Account, 'code' | 'abbr' | 'item_category'>>>;
}): V8BudgetParse {
  const { table, months, accounts } = params;
  const headerIndex = table.findIndex((r) => r.filter((c) => monthOfHeader(c ?? '') !== null).length >= 12);
  if (headerIndex < 0) return { rows: [], warning: null, error: '月の見出し(1月〜12月)の行が見つかりません。見出しの行も含めてコピーしてください' };
  const header = table[headerIndex]!;
  const monthCols = header.map((c, i) => ({ i, m: monthOfHeader(c ?? '') })).filter((x) => x.m !== null).slice(0, 12);
  const firstMonthCol = monthCols[0]!.i;

  // 書いてある年月が、選んだ年度の月と合うか
  let warning: string | null = null;
  const written = monthCols.map((x) => x.m!);
  if (written.every((w) => w.year !== null)) {
    const got = written.map((w) => `${w.year}-${String(w.month).padStart(2, '0')}`);
    if (got.join() !== months.join()) warning = `見出しの月(${got[0]}〜${got[11]})と、選んだ年度の月(${months[0]}〜${months[11]})が違います`;
  } else if (written[0]!.month !== Number(months[0]!.slice(5))) {
    warning = `見出しの最初の月(${written[0]!.month}月)が、年度の始まり(${Number(months[0]!.slice(5))}月)と違います`;
  } else {
    // 見出しが「1月」だけのときは、上の行(「2027年1月 〜 2027年12月」など)の年を読む。年が見つからなければ取り込まない
    const above = table.slice(0, headerIndex).flat().join(' ').normalize('NFKC');
    const ym = /(\d{4})\s*年\s*(\d{1,2})\s*月/.exec(above);
    if (!ym) warning = '見出しに年が書かれていません(シートの上の「2027年1月 〜」の行も含めてコピーしてください)';
    else if (`${ym[1]}-${String(Number(ym[2])).padStart(2, '0')}` !== months[0]) {
      warning = `シートの年月(${ym[1]}年${Number(ym[2])}月〜)と、選んだ年度の月(${months[0]}〜)が違います`;
    }
  }

  const findCol = (keys: string[], fallback: number) => {
    const i = header.findIndex((h, idx) => idx < firstMonthCol && keys.some((k) => nk(h ?? '').includes(nk(k))));
    return i >= 0 ? i : fallback;
  };
  const col1 = findCol(['科目①', '中項目'], firstMonthCol >= 3 ? 1 : 0);
  const col2 = findCol(['科目②', '小項目'], firstMonthCol >= 3 ? 2 : -1);
  const colK = findCol(['区分', '大項目'], col1 > 0 ? 0 : -1);

  const byName = new Map<string, { kubun: string; name: string; amounts: number[]; lineCount: number }>();
  const order: string[] = [];
  for (const r of table.slice(headerIndex + 1)) {
    const name1 = (r[col1] ?? '').trim();
    const name2 = col2 >= 0 ? (r[col2] ?? '').trim() : '';
    const kubun = colK >= 0 ? (r[colK] ?? '').trim() : '';
    if (!name1 || isSummaryRow(kubun, name1, name2)) continue;
    const amounts = monthCols.map((x) => parseBudgetAmount(r[x.i] ?? '') ?? 0);
    if (amounts.every((a) => a === 0)) continue;
    const key = `${nk(kubun)}|${nk(name1)}`;
    if (!byName.has(key)) {
      byName.set(key, { kubun, name: name1, amounts: Array(12).fill(0), lineCount: 0 });
      order.push(key);
    }
    const acc = byName.get(key)!;
    amounts.forEach((a, i) => (acc.amounts[i]! += a));
    acc.lineCount++;
  }

  const rows = order.map((key): V8BudgetRow => {
    const x = byName.get(key)!;
    return {
      kubun: x.kubun,
      name: x.name,
      accountId: matchAccount(accounts, x.kubun, x.name),
      amounts: x.amounts,
      total: x.amounts.reduce((s, a) => s + a, 0),
      lineCount: x.lineCount,
    };
  });
  return { rows, warning, error: rows.length === 0 ? '金額の入った科目がありません' : null };
}

/** v8 と Gate で呼び方が違う科目①(区分ごと。v8 の名前 → Gate の名前) */
const ALIASES: Record<string, Record<string, string>> = {
  原価: { 仕入: '仕入高' },
  販管: { 給料手当: '給与手当' },
};

/** 区分×科目①から Gate の勘定科目を1つ決める(決まらなければ null) */
export function matchAccount(
  accounts: Array<Pick<Account, 'id' | 'name' | 'active'> & Partial<Pick<Account, 'code' | 'abbr' | 'item_category'>>>,
  kubun: string,
  name: string,
): string | null {
  const active = accounts.filter((a) => a.active !== false);
  const sameKubun = kubun ? active.filter((a) => nk(a.item_category ?? '') === nk(kubun)) : active;
  // 区分が書いてあるのに同じ区分の科目が無いときは、別の区分の科目に当てない(特損→外費 などの取り違えを防ぐ)
  if (kubun && sameKubun.length === 0) return null;
  const pool = sameKubun;
  const want = [name, ALIASES[kubun.trim()]?.[name.trim()]].filter((x): x is string => !!x).map(nk);
  // 名前が同じものを先に探し、無ければコード・略称で探す(例: 「賞与」は名前の賞与を選び、略称が賞与の賞与引当金は選ばない)
  for (const fields of [(a: (typeof pool)[number]) => [a.name], (a: (typeof pool)[number]) => [a.code, a.abbr]]) {
    for (const w of want) {
      const hits = pool.filter((a) => fields(a).some((v) => !!v && nk(v) === w));
      if (hits.length === 1) return hits[0]!.id;
    }
  }
  return null;
}

/**
 * 貼り付けた文字(タブ区切り)を行×セルに。Excel・スプレッドシートは、改行を含むセル(「区分\n(大項目)」など)を
 * "…" で囲んでコピーするため、その中の改行・タブはセルの一部として読む("" は " 1つ)
 */
export function splitPasted(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let atCellStart = true;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && atCellStart) {
      quoted = true;
      atCellStart = false;
      continue;
    }
    if (ch === '\t') {
      row.push(cell);
      cell = '';
      atCellStart = true;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      atCellStart = true;
      continue;
    }
    cell += ch;
    atCellStart = false;
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
